import React, { useEffect, useState, useCallback } from 'react';
import { supervisorAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/common/Navbar';
import HighlightedSentence from '../components/common/HighlightedSentence';
import ContextModal from '../components/translation/ContextModal';
import { toast } from 'react-toastify';

// ─── Review badge ────────────────────────────────────────────────────────────
const BADGE = {
  pending:      { label: 'Pendiente',   cls: 'bg-gray-100 text-gray-600' },
  approved:     { label: 'Aprobado',    cls: 'bg-green-100 text-green-700' },
  returned:     { label: 'Devuelto',    cls: 'bg-yellow-100 text-yellow-700' },
  rejected:     { label: 'Rechazado',   cls: 'bg-red-100 text-red-700' },
  reannotated:  { label: 'Reannotado',  cls: 'bg-teal-100 text-teal-700' },
};

const StatusBadge = ({ status }) => {
  const b = BADGE[status] || BADGE.pending;
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${b.cls}`}>{b.label}</span>;
};

// ─── Progress bar ─────────────────────────────────────────────────────────────
const ProgressBar = ({ done, total }) => {
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const barW = Math.round(pct / 5);
  const color = pct >= 100 ? 'bg-green-500' : 'bg-blue-400';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-200 rounded-full h-2">
        <div className={`${color} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-600 w-16 text-right">{done}/{total}</span>
    </div>
  );
};

// ─── Review action buttons ────────────────────────────────────────────────────
const MAX_RETURNS = 3;

const ReviewButtons = ({ targetType, targetId, targetRef, currentStatus, currentNotes, returnCount = 0, onDone }) => {
  const [notes, setNotes] = useState(currentNotes || '');
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const canReturn = targetType !== 'suggestion' || returnCount < MAX_RETURNS;

  const submit = async (st) => {
    setSaving(true);
    try {
      await supervisorAPI.submitReview({
        target_type: targetType,
        target_id: targetId ?? undefined,
        target_ref: targetRef ?? undefined,
        status: st,
        notes,
      });
      toast.success({ approved: 'Aprobado', returned: 'Devuelto', rejected: 'Rechazado' }[st]);
      onDone();
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex gap-1 flex-wrap items-center">
        <button
          onClick={() => submit('approved')}
          disabled={saving || currentStatus === 'approved'}
          className="px-2 py-1 text-xs rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-40"
        >Aprobar</button>
        <button
          onClick={() => canReturn && setOpen(o => !o)}
          disabled={saving || !canReturn}
          title={!canReturn ? `Límite de ${MAX_RETURNS} devoluciones alcanzado. Debe aprobar o rechazar.` : 'Devolver con comentario'}
          className={`px-2 py-1 text-xs rounded text-white disabled:opacity-40 ${canReturn ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-gray-400 cursor-not-allowed'}`}
        >Devolver {targetType === 'suggestion' ? `(${returnCount}/${MAX_RETURNS})` : ''}</button>
        <button
          onClick={() => submit('rejected')}
          disabled={saving || currentStatus === 'rejected'}
          className="px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
        >Rechazar</button>
        {!canReturn && (
          <span className="text-xs text-orange-600 font-medium ml-1">
            ⚠ Límite alcanzado — solo puede aprobar o rechazar
          </span>
        )}
      </div>
      {open && (
        <div className="flex flex-col gap-1 mt-1">
          <textarea
            className="w-full border rounded px-2 py-1.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-yellow-400"
            placeholder="Nota para el alumno (opcional)..."
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
          <button
            onClick={() => submit('returned')}
            disabled={saving}
            className="self-end px-3 py-1.5 text-sm rounded bg-yellow-500 text-white hover:bg-yellow-600 disabled:opacity-40"
          >{saving ? '...' : 'Enviar devolución'}</button>
        </div>
      )}
    </div>
  );
};

// ─── Context button (reusable for any card that has a translation_id) ─────────
const ContextButton = ({ translationId, direction }) => {
  const [show, setShow] = useState(false);
  if (!translationId) return null;
  return (
    <>
      <button
        onClick={() => setShow(true)}
        className="px-4 py-1.5 text-sm font-medium border border-gray-300 rounded-md text-gray-600 bg-white hover:bg-gray-50 hover:border-gray-400 hover:text-gray-800 transition-colors shadow-sm cursor-pointer"
        title="Ver oraciones anteriores y siguientes para contexto"
      >Ver contexto</button>
      <ContextModal
        isOpen={show}
        onClose={() => setShow(false)}
        translation={{ id: translationId, direction }}
      />
    </>
  );
};

// ─── Tab: Validaciones ────────────────────────────────────────────────────────
const ValidationsTab = () => {
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const PAGE = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await supervisorAPI.getStudentsValidations({ skip: page * PAGE, limit: PAGE, review_status: filter || undefined });
      setData(res.data);
    } catch { toast.error('Error cargando validaciones'); }
    finally { setLoading(false); }
  }, [page, filter]);

  useEffect(() => { load(); }, [load]);

  const CORRECTNESS = { correct: 'Correcta', gn_more: 'Más guaraní', es_more: 'Más español', incorrect: 'Incorrecta' };
  const DIR_LABEL = { 'sp-gn': 'ES→GN', 'gn-sp': 'GN→ES' };
  const CAT_LABELS = {
    guarani_estructura_guarani:    "GN Est. Guaraní",
    guarani_estructura_castellano: "GN Est. Castellano",
    jopara:                        "Jopara",
    jehea:                         "Jehe'a",
  };

  return (
    <div>
      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {['', 'pending', 'approved', 'returned', 'rejected'].map(s => (
          <button
            key={s}
            onClick={() => { setFilter(s); setPage(0); }}
            className={`px-3 py-1 rounded text-sm border ${filter === s ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
          >{s === '' ? 'Todos' : BADGE[s]?.label}</button>
        ))}
      </div>

      {loading ? <div className="text-gray-500 text-sm">Cargando...</div> : data.items.length === 0 ? (
        <div className="text-gray-500 text-sm">No hay validaciones.</div>
      ) : (
        <div className="space-y-3">
          {data.items.map(v => {
            const dir = v.direction || 'sp-gn';
            const leftLabel = dir === 'sp-gn' ? 'Español (original)' : 'Guaraní (original)';
            const rightLabel = dir === 'sp-gn' ? 'Guaraní (traducción)' : 'Español (traducción)';
            const leftText  = dir === 'sp-gn' ? v.spanish_text  : v.guarani_text;
            const rightText = dir === 'sp-gn' ? v.guarani_text  : v.spanish_text;
            return (
              <div key={v.id} className="border rounded-lg p-4 bg-white shadow-sm">
                {/* Header */}
                <div className="flex flex-wrap gap-2 items-center mb-3">
                  <span className="font-medium text-sm">{v.student_username}</span>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-mono">{DIR_LABEL[dir] || dir}</span>
                  <StatusBadge status={v.review_status} />
                  <span className="text-xs text-gray-400 ml-auto">{new Date(v.created_at).toLocaleDateString()}</span>
                </div>

                {/* Student evaluation summary */}
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-3 text-sm">
                  <p className="text-xs font-semibold text-blue-700 mb-1.5 uppercase tracking-wide">La evaluación del alumno para esta traducción fue:</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-0.5 rounded bg-white border border-blue-200 text-blue-700 text-xs">
                      Correctitud: {CORRECTNESS[v.correctness] || v.correctness}
                    </span>
                    <span className="px-2 py-0.5 rounded bg-white border border-blue-200 text-blue-700 text-xs">
                      Fluidez: {v.fluency_score}/5
                    </span>
                    <span className="px-2 py-0.5 rounded bg-white border border-blue-200 text-blue-700 text-xs">
                      Precisión: {v.accuracy_score}/5
                    </span>
                    {v.category && (
                      <span className="px-2 py-0.5 rounded bg-purple-50 border border-purple-200 text-purple-700 text-xs">
                        Guaraní: {CAT_LABELS[v.category] || v.category}
                      </span>
                    )}
                  </div>
                </div>

                {/* Text panels — direction-aware */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                  <div className="bg-gray-50 rounded p-3 text-sm">
                    <p className="font-medium text-gray-500 text-xs mb-1">{leftLabel}</p>
                    <p className="text-gray-800">{leftText || '—'}</p>
                  </div>
                  <div className="bg-gray-50 rounded p-3 text-sm">
                    <p className="font-medium text-gray-500 text-xs mb-1">{rightLabel}</p>
                    <p className="text-gray-800">{rightText || '—'}</p>
                  </div>
                </div>

                {/* Comments — 2 columns */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                  <div className="rounded-lg border border-gray-200 p-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Comentarios del alumno</p>
                    {v.notes
                      ? <p className="text-sm text-gray-700 italic">"{v.notes}"</p>
                      : <p className="text-xs text-gray-400">Sin comentarios</p>}
                  </div>
                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                    <p className="text-xs font-semibold text-yellow-700 uppercase tracking-wide mb-1">Comentarios del docente</p>
                    {v.review_notes
                      ? <p className="text-sm text-yellow-800 italic">"{v.review_notes}"</p>
                      : <p className="text-xs text-yellow-400">Sin comentarios</p>}
                  </div>
                </div>

                <div className="flex justify-center mb-3">
                  <ContextButton translationId={v.translation_id} direction={v.direction} />
                </div>
                <ReviewButtons
                  targetType="validation"
                  targetId={v.id}
                  currentStatus={v.review_status}
                  currentNotes={v.review_notes}
                  onDone={load}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {data.total > PAGE && (
        <div className="flex justify-between items-center mt-4 text-sm">
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border rounded disabled:opacity-40">← Anterior</button>
          <span className="text-gray-500">{page * PAGE + 1}–{Math.min((page + 1) * PAGE, data.total)} de {data.total}</span>
          <button disabled={(page + 1) * PAGE >= data.total} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-40">Siguiente →</button>
        </div>
      )}
    </div>
  );
};

// ─── Suggestion card (needs own state for history accordion) ──────────────────
const SuggestionCard = ({ s, dir, origLeftLabel, origRightLabel, origLeftText, origRightText, history, catInfo, CAT_LABELS, DIR_LABEL, onDone }) => {
  const [histOpen, setHistOpen] = useState(false);
  return (
    <div className="border rounded-lg p-4 bg-white shadow-sm">
      {/* Header */}
      <div className="flex flex-wrap gap-2 items-center mb-3">
        <span className="font-medium text-sm">{s.student_username}</span>
        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-mono">{DIR_LABEL[dir] || dir}</span>
        {s.return_count > 0 && (
          <span className={`text-xs px-2 py-0.5 rounded font-medium ${s.return_count >= 3 ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
            Devoluciones: {s.return_count}/{MAX_RETURNS}
          </span>
        )}
        <StatusBadge status={s.review_status} />
        <span className="text-xs text-gray-400 ml-auto">{new Date(s.created_at).toLocaleDateString()}</span>
      </div>

      {/* Original text — direction-aware */}
      <div className="mb-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Traducción original</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="bg-gray-50 rounded p-3 text-sm">
            <p className="font-medium text-gray-500 text-xs mb-1">{origLeftLabel}</p>
            <p className="text-gray-800">{origLeftText || '—'}</p>
          </div>
          <div className="bg-gray-50 rounded p-3 text-sm">
            <p className="font-medium text-gray-500 text-xs mb-1">{origRightLabel}</p>
            <p className="text-gray-800">{origRightText || '—'}</p>
          </div>
        </div>
      </div>

      {/* Current suggestion */}
      <div className="mb-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
          Sugerencia actual
          {history.length > 0 && <span className="ml-1 text-yellow-600 normal-case font-normal">(versión {history.length + 1})</span>}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {s.suggested_guarani && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
              <div className="flex items-center gap-2 mb-1">
                <p className="font-medium text-yellow-700 text-xs">Sugerencia GN</p>
                {catInfo && (
                  <span className={`px-2 py-0.5 rounded border text-xs font-medium ${catInfo.color}`}>
                    {catInfo.label}
                  </span>
                )}
              </div>
              <p className="text-gray-800">{s.suggested_guarani}</p>
            </div>
          )}
          {s.suggested_spanish && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
              <p className="font-medium text-yellow-700 text-xs mb-1">Sugerencia ES</p>
              <p className="text-gray-800">{s.suggested_spanish}</p>
            </div>
          )}
        </div>
      </div>

      {/* Comments — 2 columns */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
        <div className="rounded-lg border border-gray-200 p-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Comentarios del alumno</p>
          {s.reason
            ? <p className="text-sm text-gray-700 italic">"{s.reason}"</p>
            : <p className="text-xs text-gray-400">Sin comentarios</p>}
        </div>
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
          <p className="text-xs font-semibold text-yellow-700 uppercase tracking-wide mb-1">Comentarios del docente</p>
          {s.review_notes
            ? <p className="text-sm text-yellow-800 italic">"{s.review_notes}"</p>
            : <p className="text-xs text-yellow-400">Sin comentarios</p>}
        </div>
      </div>

      {history.length > 0 && (
        <div className="mb-3">
          <button
            onClick={() => setHistOpen(o => !o)}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
          >
            {histOpen ? '▼' : '▶'} Historial de devoluciones ({history.length})
          </button>
          {histOpen && (
            <div className="mt-2 space-y-2 border-l-2 border-yellow-300 pl-3">
              {history.map((h, i) => {
                const hCatInfo = h.suggested_category ? CAT_LABELS[h.suggested_category] : null;
                return (
                  <div key={i} className="bg-yellow-50 rounded p-3 text-xs">
                    <div className="flex items-center gap-2 mb-1 text-yellow-700 font-semibold">
                      <span>↩ Devolución #{h.version}</span>
                      <span className="font-normal text-gray-500">{h.returned_at ? new Date(h.returned_at).toLocaleString() : ''}</span>
                    </div>
                    {h.suggested_guarani && (
                      <p className="text-gray-700 mb-0.5">
                        <span className="font-medium">GN: </span>{h.suggested_guarani}
                        {hCatInfo && <span className={`inline-block ml-2 px-1.5 py-0.5 rounded border text-xs ${hCatInfo.color}`}>{hCatInfo.label}</span>}
                      </p>
                    )}
                    {h.suggested_spanish && <p className="text-gray-700 mb-0.5"><span className="font-medium">ES: </span>{h.suggested_spanish}</p>}
                    {h.supervisor_note && (
                      <p className="text-yellow-700 italic mt-1">📝 Nota del supervisor: "{h.supervisor_note}"</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="flex justify-center mb-3">
        <ContextButton translationId={s.translation_id} direction={s.direction} />
      </div>
      <ReviewButtons
        targetType="suggestion"
        targetId={s.id}
        currentStatus={s.review_status}
        currentNotes={s.review_notes}
        returnCount={s.return_count || 0}
        onDone={onDone}
      />
    </div>
  );
};

// ─── Tab: Sugerencias ─────────────────────────────────────────────────────────
const SuggestionsTab = () => {
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const PAGE = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await supervisorAPI.getStudentsSuggestions({ skip: page * PAGE, limit: PAGE, review_status: filter || undefined });
      setData(res.data);
    } catch { toast.error('Error cargando sugerencias'); }
    finally { setLoading(false); }
  }, [page, filter]);

  useEffect(() => { load(); }, [load]);

  const DIR_LABEL = { 'sp-gn': 'ES→GN', 'gn-sp': 'GN→ES' };
  const CAT_LABELS = {
    guarani_estructura_guarani:   { label: 'GN c/ Est. Guaraní',    color: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
    guarani_estructura_castellano:{ label: 'GN c/ Est. Castellano',  color: 'bg-teal-100 text-teal-800 border-teal-300' },
    jopara:                       { label: 'Jopara',                  color: 'bg-green-100 text-green-800 border-green-300' },
    jehea:                        { label: "Jehe'a",                  color: 'bg-orange-100 text-orange-800 border-orange-300' },
  };

  return (
    <div>
      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {['', 'pending', 'approved', 'returned', 'rejected'].map(st => (
          <button
            key={st}
            onClick={() => { setFilter(st); setPage(0); }}
            className={`px-3 py-1 rounded text-sm border ${filter === st ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
          >{st === '' ? 'Todos' : BADGE[st]?.label}</button>
        ))}
      </div>

      {loading ? <div className="text-gray-500 text-sm">Cargando...</div> : data.items.length === 0 ? (
        <div className="text-gray-500 text-sm">No hay sugerencias.</div>
      ) : (
        <div className="space-y-3">
          {data.items.map(s => {
            const dir = s.direction || 'sp-gn';
            const origLeftLabel  = dir === 'sp-gn' ? 'Original ES' : 'Original GN';
            const origRightLabel = dir === 'sp-gn' ? 'Original GN' : 'Original ES';
            const origLeftText   = dir === 'sp-gn' ? s.spanish_text  : s.guarani_text;
            const origRightText  = dir === 'sp-gn' ? s.guarani_text  : s.spanish_text;
            const history = Array.isArray(s.return_history) ? s.return_history : [];
            const catInfo = s.suggested_category ? CAT_LABELS[s.suggested_category] : null;
            return (
              <SuggestionCard
                key={s.id}
                s={s}
                dir={dir}
                origLeftLabel={origLeftLabel}
                origRightLabel={origRightLabel}
                origLeftText={origLeftText}
                origRightText={origRightText}
                history={history}
                catInfo={catInfo}
                CAT_LABELS={CAT_LABELS}
                DIR_LABEL={DIR_LABEL}
                onDone={load}
              />
            );
          })}
        </div>
      )}

      {data.total > PAGE && (
        <div className="flex justify-between items-center mt-4 text-sm">
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border rounded disabled:opacity-40">← Anterior</button>
          <span className="text-gray-500">{page * PAGE + 1}–{Math.min((page + 1) * PAGE, data.total)} de {data.total}</span>
          <button disabled={(page + 1) * PAGE >= data.total} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-40">Siguiente →</button>
        </div>
      )}
    </div>
  );
};

// ─── Searchable annotator select ──────────────────────────────────────────────
const AnnotatorSelect = ({ annotators, value, onChange }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = annotators.filter(u => u.toLowerCase().includes(search.toLowerCase()));
  const label = value || 'Todos';

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-1.5 rounded border border-purple-300 bg-white text-sm text-purple-800 hover:bg-purple-50 min-w-[140px]"
      >
        <span className="flex-1 text-left truncate">{label}</span>
        <svg className="w-4 h-4 text-purple-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              autoFocus
              type="text"
              placeholder="Buscar anotador..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
          </div>
          <ul className="max-h-52 overflow-y-auto py-1">
            <li>
              <button
                onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-purple-50 ${value === '' ? 'text-purple-700 font-semibold' : 'text-gray-700'}`}
              >Todos</button>
            </li>
            {filtered.map(u => (
              <li key={u}>
                <button
                  onClick={() => { onChange(u); setOpen(false); setSearch(''); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-purple-50 ${value === u ? 'text-purple-700 font-semibold' : 'text-gray-700'}`}
                >{u}</button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-400 italic">Sin resultados</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

// ─── Tab: Prodigy ─────────────────────────────────────────────────────────────
const ATYPE_LABELS = { pos: 'POS', features: 'Features', relations: 'Relaciones', ner: 'NER' };
const ATYPE_COLORS = {
  pos:       'bg-blue-50 text-blue-700 border-blue-200',
  features:  'bg-purple-50 text-purple-700 border-purple-200',
  relations: 'bg-orange-50 text-orange-700 border-orange-200',
  ner:       'bg-green-50 text-green-700 border-green-200',
};

const TagChip = ({ text, tag, color }) => (
  <span className={`inline-flex flex-col items-center px-1.5 py-0.5 rounded border text-xs mr-1 mb-1 ${color}`}>
    <span className="font-medium">{text}</span>
    <span className="text-[10px] opacity-70">{tag}</span>
  </span>
);

const ProdigyTab = () => {
  const { user } = useAuth();
  const isAdmin = user?.is_superuser;

  const [data, setData] = useState({ total: 0, items: [], batches: [], annotators: [] });
  const [allAnnotators, setAllAnnotators] = useState([]);
  const [pendingKeys, setPendingKeys] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [atypeFilter, setAtypeFilter] = useState('');
  const [batchFilter, setBatchFilter] = useState('');
  const [annotatorFilter, setAnnotatorFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const PAGE = 20;

  const annKey = (ann) => `${ann.username}::${ann.atype}::${ann.text}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, pendingRes] = await Promise.all([
        supervisorAPI.getProdigyAnnotations({
          skip: page * PAGE,
          limit: PAGE,
          annotation_type: atypeFilter || undefined,
          review_status: filter || undefined,
          batch: batchFilter !== '' ? Number(batchFilter) : undefined,
          sort: sortOrder,
          username_filter: annotatorFilter || undefined,
        }),
        supervisorAPI.getProdigyAnnotations({
          skip: 0,
          limit: 500,
          review_status: 'pending',
        }),
      ]);
      setData(res.data);
      // Only update the full annotator list when no filter is active
      if (!annotatorFilter && (res.data.annotators || []).length > 0) {
        setAllAnnotators(res.data.annotators);
      }
      setPendingKeys(new Set((pendingRes.data.items || []).map(annKey)));
    } catch {
      toast.error('Error cargando anotaciones de Prodigy');
    } finally {
      setLoading(false);
    }
  }, [page, filter, atypeFilter, batchFilter, sortOrder, annotatorFilter]);

  useEffect(() => { load(); }, [load]);

  const renderSummary = (atype, summary) => {
    if (!summary) return null;
    const color = ATYPE_COLORS[atype] || 'bg-gray-50 text-gray-700 border-gray-200';
    if (atype === 'relations') {
      const rels = summary.relations || [];
      if (!rels.length) return <span className="text-xs text-gray-400">Sin relaciones</span>;
      return (
        <div className="flex flex-wrap gap-1">
          {rels.map((r, i) => (
            <span key={i} className={`inline-flex items-center gap-1 border rounded px-1.5 py-0.5 text-xs ${color}`}>
              <span className="font-medium">{r.head}</span>
              <span className="opacity-60">→{r.label}→</span>
              <span className="font-medium">{r.child}</span>
            </span>
          ))}
        </div>
      );
    }
    const tagged = summary.tagged_tokens || [];
    if (!tagged.length) return <span className="text-xs text-gray-400">Sin etiquetas</span>;
    return (
      <div className="flex flex-wrap">
        {tagged.map((t, i) => <TagChip key={i} text={t.text} tag={t.tag} color={color} />)}
      </div>
    );
  };

  // Merge consecutive tokens of the same span (or untagged runs) into one segment.
  // Features are tokenized per character, so without this every letter is its own chip.
  const groupTokens = (tokens) => {
    const groups = [];
    tokens.forEach(tok => {
      const key = tok.tag ? `${tok.span ?? ''}:${tok.tag}` : null;
      const piece = tok.text + (tok.ws ? ' ' : '');
      const last = groups[groups.length - 1];
      if (last && last.key === key) last.text += piece;
      else groups.push({ key, tag: tok.tag, text: piece });
    });
    return groups;
  };

  // Renders the full sentence in reading order, annotated segments highlighted with their label
  const renderAnnotatedSentence = (atype, summary) => {
    if (!summary || atype === 'relations') return null;
    const allTokens = summary.annotated_tokens;
    if (!allTokens || !allTokens.length) return null;
    const color = ATYPE_COLORS[atype] || 'bg-gray-50 text-gray-700 border-gray-200';
    const groups = groupTokens(allTokens);
    const taggedCount = groups.filter(g => g.tag).length;

    // Features (por carácter): cada palabra en su propia caja con fondo, para ver qué
    // caracteres pertenecen a qué palabra. Dentro de la caja, cada tramo etiquetado
    // lleva su etiqueta y lo que quedó sin etiquetar se ve apagado.
    if (atype === 'features') {
      const words = [];
      let cur = [];
      allTokens.forEach(t => {
        if (!t.text || /^\s+$/.test(t.text)) {
          if (cur.length) { words.push(cur); cur = []; }
        } else cur.push(t);
      });
      if (cur.length) words.push(cur);
      return (
        <div className="mt-2 bg-white border border-gray-200 rounded p-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Oración anotada</span>
            <span className="text-xs text-gray-400">
              {words.length} palabras · {taggedCount} {taggedCount === 1 ? 'etiqueta' : 'etiquetas'}
            </span>
          </div>
          <div className="text-sm text-gray-600 leading-normal flex flex-wrap gap-x-2 gap-y-2">
            {words.map((w, wi) => {
              const wordText = w.map(t => t.text).join('');
              const wordGroups = groupTokens(w);
              const untagged = wordGroups.every(g => !g.tag);
              return (
                <span
                  key={wi}
                  title={wordText}
                  className={`inline-flex items-stretch rounded-md border px-1 py-1 gap-px ${
                    untagged ? 'bg-gray-50 border-dashed border-gray-300' : 'bg-slate-100 border-slate-300'
                  }`}
                >
                  {wordGroups.map((g, gi) => (
                    <span
                      key={gi}
                      className={`inline-flex flex-col items-center justify-between px-1 rounded leading-tight ${
                        g.tag ? `border ${color}` : 'text-gray-400'
                      }`}
                    >
                      <span className="text-sm font-medium">{g.text}</span>
                      <span className="text-[10px] opacity-75 mt-0.5 whitespace-nowrap">{g.tag || '·'}</span>
                    </span>
                  ))}
                </span>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div className="mt-2 bg-white border border-gray-200 rounded p-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Oración anotada</span>
          <span className="text-xs text-gray-400">{taggedCount} {taggedCount === 1 ? 'etiqueta' : 'etiquetas'}</span>
        </div>
        <div className="text-sm text-gray-600 leading-[2.75rem]">
          {groups.map((g, i) => (
            g.tag ? (
              <React.Fragment key={i}>
                {/^\s/.test(g.text) && ' '}
                <span className={`inline-flex flex-col items-center align-baseline px-1.5 py-0.5 mx-px rounded border leading-tight ${color}`}>
                  <span className="text-sm font-medium">{g.text.trim()}</span>
                  <span className="text-[10px] opacity-75 mt-0.5 whitespace-nowrap">{g.tag}</span>
                </span>
                {/\s$/.test(g.text) && ' '}
              </React.Fragment>
            ) : (
              <span key={i}>{g.text}</span>
            )
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Filters */}
      <div className="flex gap-2 mb-3 flex-wrap items-center">
        <span className="text-xs text-gray-500 font-medium">Estado:</span>
        {['pending', 'returned', 'approved', 'rejected', 'reannotated', ''].map(s => (
          <button
            key={s}
            onClick={() => { setFilter(s); setPage(0); }}
            className={`px-3 py-1 rounded text-sm border ${filter === s ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
          >{s === '' ? 'Todos' : BADGE[s]?.label}</button>
        ))}
      </div>
      <div className="flex gap-2 mb-3 flex-wrap items-center">
        <span className="text-xs text-gray-500 font-medium">Tipo:</span>
        {['', 'pos', 'features', 'relations', 'ner'].map(t => (
          <button
            key={t}
            onClick={() => { setAtypeFilter(t); setPage(0); }}
            className={`px-3 py-1 rounded text-sm border ${atypeFilter === t ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
          >{t === '' ? 'Todos' : ATYPE_LABELS[t] || t.toUpperCase()}</button>
        ))}
      </div>
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <span className="text-xs text-gray-500 font-medium">Batch:</span>
        <button
          onClick={() => { setBatchFilter(''); setPage(0); }}
          className={`px-3 py-1 rounded text-sm border ${batchFilter === '' ? 'bg-teal-600 text-white border-teal-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
        >Todos</button>
        {(data.batches || []).map(b => (
          <button
            key={b}
            onClick={() => { setBatchFilter(String(b)); setPage(0); }}
            className={`px-3 py-1 rounded text-sm border ${batchFilter === String(b) ? 'bg-teal-600 text-white border-teal-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
          >Batch {String(b).padStart(3, '0')}</button>
        ))}
        <span className="ml-3 text-xs text-gray-500 font-medium">Orden:</span>
        <button
          onClick={() => { setSortOrder(o => o === 'desc' ? 'asc' : 'desc'); setPage(0); }}
          className="px-3 py-1 rounded text-sm border border-gray-300 text-gray-600 hover:bg-gray-50"
        >{sortOrder === 'desc' ? '↓ Más recientes' : '↑ Más antiguas'}</button>
      </div>

      {isAdmin && allAnnotators.length > 0 && (
        <div className="flex gap-2 mb-4 flex-wrap items-center bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
          <span className="text-xs text-purple-700 font-semibold">Anotador:</span>
          <AnnotatorSelect
            annotators={allAnnotators}
            value={annotatorFilter}
            onChange={(v) => { setAnnotatorFilter(v); setPage(0); }}
          />
        </div>
      )}

      {loading ? (
        <div className="text-gray-500 text-sm">Cargando anotaciones...</div>
      ) : data.items.length === 0 ? (
        <div className="text-gray-500 text-sm">No hay anotaciones de Prodigy.</div>
      ) : (
        <div className="space-y-3">
          {(() => {
            return data.items.map(ann => {
              const isReturnedWithPending =
                ann.review_status === 'returned' && pendingKeys.has(annKey(ann));
              const isReannotated = ann.review_status === 'reannotated';
              const hideButtons = isReturnedWithPending || isReannotated;

              return (
                <div
                  key={ann.target_ref}
                  className={`border rounded-lg p-4 bg-white shadow-sm ${isReturnedWithPending ? 'border-yellow-300 opacity-75' : isReannotated ? 'border-green-300 opacity-75' : ''}`}
                >
                  {isReturnedWithPending && (
                    <div className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-2 py-1 mb-2">
                      Versión original devuelta — el alumno ya envió una re-anotación (ver abajo)
                    </div>
                  )}
                  {isReannotated && (
                    <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1 mb-2">
                      Versión original devuelta — el alumno completó la re-anotación
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 items-center mb-2">
                    <span className="font-medium text-sm">{ann.username}</span>
                    <span className={`text-xs px-2 py-0.5 rounded border ${ATYPE_COLORS[ann.atype] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                      {ATYPE_LABELS[ann.atype] || ann.atype}
                    </span>
                    {ann.batch && (
                      <span className="text-xs text-gray-500">Batch {String(ann.batch).padStart(3, '0')}</span>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded ${ann.answer === 'accept' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {ann.answer === 'accept' ? 'Aceptada' : ann.answer === 'reject' ? 'Rechazada' : ann.answer}
                    </span>
                    <StatusBadge status={ann.review_status} />
                    {ann.reviewed_by && ann.reviewed_by !== user?.username && (
                      <span className="text-xs text-gray-500">por {ann.reviewed_by}</span>
                    )}
                  </div>

                  {/* Sentence text — relaciones (o sin annotated_tokens): resalta lo anotado */}
                  <div className="bg-gray-50 rounded p-2 text-sm mb-2 text-gray-700 leading-relaxed">
                    {(ann.atype === 'relations' || !ann.summary?.annotated_tokens?.length) && ann.summary?.marks?.length ? (() => {
                      // clean_text es un prefijo (con strip) de text: corregir los offsets
                      const clean = ann.summary.clean_text || ann.text;
                      const shift = Math.max(0, (ann.text || '').indexOf(clean));
                      const marks = ann.summary.marks.map((m) => ({ ...m, start: m.start - shift, end: m.end - shift }));
                      return <HighlightedSentence text={clean} marks={marks} atype={ann.atype} />;
                    })() : (ann.summary?.clean_text || ann.text)}
                  </div>

                  {/* Annotated sentence inline (NC-54): shown for POS/features/NER */}
                  {renderAnnotatedSentence(ann.atype, ann.summary)}

                  {/* Chips summary: only for relations (or fallback when no annotated_tokens) */}
                  {(ann.atype === 'relations' || !ann.summary?.annotated_tokens?.length) && (
                    <div className="mt-2 mb-1">
                      {renderSummary(ann.atype, ann.summary)}
                    </div>
                  )}

                  {ann.review_notes && (
                    <div className="text-xs text-yellow-700 italic mb-2">
                      {ann.reviewed_by && ann.reviewed_by !== user?.username ? `Nota de ${ann.reviewed_by}` : 'Tu nota'}: "{ann.review_notes}"
                    </div>
                  )}

                  {!hideButtons && (
                    <ReviewButtons
                      targetType="prodigy"
                      targetId={null}
                      targetRef={ann.target_ref}
                      currentStatus={ann.review_status}
                      currentNotes={ann.review_notes}
                      onDone={load}
                    />
                  )}
                </div>
              );
            });
          })()}
        </div>
      )}

      {data.total > PAGE && (
        <div className="flex justify-between items-center mt-4 text-sm">
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border rounded disabled:opacity-40">← Anterior</button>
          <span className="text-gray-500">{page * PAGE + 1}–{Math.min((page + 1) * PAGE, data.total)} de {data.total}</span>
          <button disabled={(page + 1) * PAGE >= data.total} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-40">Siguiente →</button>
        </div>
      )}
    </div>
  );
};

// ─── Tab: Progreso Prodigy ────────────────────────────────────────────────────
// Histórico general (no por batch): oraciones únicas con ≥1 etiqueta y palabras
// etiquetadas por alumno, más el avance de cada grupo dentro de su rango de batches.
const ProdigyProgressTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    supervisorAPI.getProdigyProgress()
      .then(res => { setData(res.data); })
      .catch(() => { setError('No se pudo cargar el progreso.'); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-gray-500 text-sm">Cargando progreso...</div>;
  if (error)   return <div className="text-red-500 text-sm">{error}</div>;
  if (!data)   return null;

  if (data.error) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800 text-sm">
        <strong>Prodigy Manager no disponible.</strong>
        <p className="mt-1 text-xs">{data.error}</p>
      </div>
    );
  }

  const { by_annotator: byAnnotator = [], groups = [], supervised_users: supervisedUsers = [] } = data;

  if (supervisedUsers.length === 0) {
    return <div className="text-gray-500 text-sm">No tenés alumnos asignados.</div>;
  }

  const rowsByUser = {};
  byAnnotator.forEach(r => { (rowsByUser[r.annotator] = rowsByUser[r.annotator] || []).push(r); });
  const thCls = 'text-left text-xs font-semibold text-gray-500 px-3 py-2 border-b';
  const tdCls = 'px-3 py-2 border-b border-gray-100';

  return (
    <div className="space-y-6">
      {/* Grupos de los alumnos */}
      {groups.length > 0 && (
        <div className="border rounded-lg p-4 bg-white shadow-sm">
          <h3 className="font-semibold text-gray-800">Grupos</h3>
          <p className="text-xs text-gray-400 mb-3">Oraciones anotadas por cada alumno dentro del rango de batches del grupo</p>
          <div className="space-y-4">
            {groups.map(g => (
              <div key={g.svc_id}>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="text-sm font-medium text-gray-800">{g.name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded border ${ATYPE_COLORS[g.annotation_type] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                    {ATYPE_LABELS[g.annotation_type] || g.annotation_type}
                  </span>
                  <span className="text-xs text-gray-500">Batches {g.start_batch}–{g.end_batch} · {(g.batches_with_data || []).length}/{g.total_batches} con anotaciones · {(g.running_batches || []).length} activos</span>
                </div>
                <div className="space-y-1.5">
                  {g.members.map(m => (
                    <div key={m} className="flex items-center gap-3">
                      <span className="text-sm w-32 truncate text-gray-700" title={m}>{m}</span>
                      <div className="flex-1"><ProgressBar done={g.per_member_sentences[m] || 0} total={g.range_sentences} /></div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Histórico por alumno */}
      <div className="border rounded-lg bg-white shadow-sm overflow-hidden">
        <div className="px-4 pt-4">
          <h3 className="font-semibold text-gray-800">Por alumno</h3>
          <p className="text-xs text-gray-400 mb-3">
            Histórico completo: oraciones únicas con al menos una etiqueta y palabras etiquetadas (solo respuestas aceptadas)
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="bg-gray-50">
              <tr>
                {['Alumno', 'Tipo', 'Oraciones', 'Palabras etiquetadas'].map(h => <th key={h} className={thCls}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {supervisedUsers.map(username => {
                const rows = rowsByUser[username];
                if (!rows) {
                  return (
                    <tr key={username}>
                      <td className={`${tdCls} text-gray-500`}>{username}</td>
                      <td className={`${tdCls} text-xs text-gray-400 italic`} colSpan={3}>Sin anotaciones</td>
                    </tr>
                  );
                }
                return rows.map((r, i) => (
                  <tr key={`${username}|${r.annotation_type}`}>
                    <td className={`${tdCls} font-medium text-gray-700`}>{i === 0 ? username : ''}</td>
                    <td className={tdCls}>
                      <span className={`text-xs px-2 py-0.5 rounded border ${ATYPE_COLORS[r.annotation_type] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                        {ATYPE_LABELS[r.annotation_type] || r.annotation_type}
                      </span>
                    </td>
                    <td className={`${tdCls} font-semibold text-gray-900`}>{r.sentences}</td>
                    <td className={`${tdCls} text-gray-600`}>
                      {r.tagged_words} <span className="text-xs text-gray-400">/ {r.words_in_sentences} ({r.word_coverage_pct}%)</span>
                    </td>
                  </tr>
                ));
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'validations', label: 'Validaciones' },
  { key: 'suggestions', label: 'Sugerencias' },
  { key: 'prodigy',     label: 'Prodigy' },
  { key: 'progreso',    label: 'Progreso Prodigy' },
];

const SupervisorPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.is_superuser;
  const [tab, setTab] = useState('validations');

  if (!user) return null;

  return (
    <div>
      <Navbar />
      <div className="max-w-5xl mx-auto p-6">
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold">Panel del Supervisor</h2>
            {isAdmin && (
              <span className="px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-300">
                Vista Admin — todos los anotadores
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {isAdmin
              ? 'Ves todas las anotaciones y validaciones de todos los anotadores del sistema.'
              : 'Revisá las validaciones y sugerencias de tus alumnos. Podés aprobar, devolver o rechazar cada una.'}
          </p>
        </div>

        {/* Tab bar */}
        <div className="flex border-b mb-6">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-5 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >{t.label}</button>
          ))}
        </div>

        {tab === 'validations' && <ValidationsTab />}
        {tab === 'suggestions' && <SuggestionsTab />}
        {tab === 'prodigy'     && <ProdigyTab />}
        {tab === 'progreso'    && <ProdigyProgressTab />}
      </div>
    </div>
  );
};

export default SupervisorPage;
